import React, { useEffect, useState } from 'react'

import ProviderService from '../../../services/ProviderService'
import validation from '../../../validation'
import AddButton from '../../controls/AddButton'
import DatePicker from '../../controls/DatePicker'
import RemoveButton from '../../controls/RemoveButton'

export default {
  name: 'Diagnoses',
  group: 'Case Details',
  validationSchema: getValidationSchema(),
  Component,
}

function getValidationSchema() {
  return validation.object({
    diagnosis: validation
      .arrayOf(
        validation.object({
          name: validation.name('Diagnosis').required(),
          physician: validation.shortText('Physician'),
          date: validation.date(),
        }),
      )
      .nullable(),
  })
}

function Component({
  addItemToArray,
  removeItemFromArray,
  createInputProps,
  getValue,
  ErrorMessage,
  caseModel,
  caseNumber,
}) {
  const [editingIndex, setEditingIndex] = useState(null)
  const [caseProviders, setCaseProviders] = useState([])

  const cId = caseNumber || caseModel?.caseNumber || caseModel?.CaseNumber || caseModel?.id || caseModel?.Id

  useEffect(() => {
    if (cId && cId !== 'N/A') {
      ProviderService.getProvidersForCase(cId)
        .then(allProviders => {
          const treatmentOnly = (allProviders || []).filter(
            p =>
              !p.type ||
              (p.type && !p.type.toLowerCase().includes('pharmacy') && !p.type.toLowerCase().includes('equipment')),
          )
          setCaseProviders(treatmentOnly)
        })
        .catch(err => {
          // eslint-disable-next-line no-console
          console.warn(err)
        })
    } else if (caseModel?.treatmentProviders) {
      setCaseProviders(caseModel.treatmentProviders)
    }
  }, [cId, caseModel])

  const allDiagnoses = getValue('diagnosis', []).map((x, index) => ({
    index,
    diagnosis: x,
  }))

  const groupProps = {
    createInputProps,
    ErrorMessage,
    removeItemFromArray,
    editingIndex,
    setEditingIndex,
    getValue,
    caseProviders,
  }

  return (
    <div className="Diagnosis-component">
      <h4 className="pb-3">Diagnoses</h4>

      <DiagnosisGroup
        diagnosis={allDiagnoses}
        {...groupProps}
        onAdd={() => {
          const index = addItemToArray('diagnosis', {})
          setEditingIndex(index)
        }}
      />
    </div>
  )
}

function DiagnosisGroup({
  diagnosis,
  removeItemFromArray,
  onAdd,
  editingIndex,
  setEditingIndex,
  caseProviders,
  ...rest
}) {
  return (
    <>
      <div>
        {diagnosis.map(x => {
          const namePrefix = `diagnosis[${x.index}]`
          const isEditing = editingIndex === x.index
          return (
            <React.Fragment key={namePrefix}>
              {diagnosis.length > 0 ? <div className="mt-3"></div> : null}
              <Diagnosis
                namePrefix={namePrefix}
                flatIndex={x.index}
                isEditing={isEditing}
                caseProviders={caseProviders}
                onEdit={() => setEditingIndex(x.index)}
                onDone={() => setEditingIndex(null)}
                handleRemove={() => {
                  removeItemFromArray('diagnosis', x.index)
                  if (editingIndex === x.index) {
                    setEditingIndex(null)
                  }
                }}
                {...rest}
              />
              <hr />
            </React.Fragment>
          )
        })}
        {onAdd && <AddButton onClick={onAdd}>Add Diagnosis</AddButton>}
      </div>
    </>
  )
}

function Diagnosis({
  namePrefix,
  flatIndex,
  isEditing,
  onEdit,
  onDone,
  handleRemove,
  createInputProps,
  ErrorMessage,
  getValue,
  caseProviders = [],
}) {
  const name = getValue(`${namePrefix}.name`, '')
  const physician = getValue(`${namePrefix}.physician`, '')
  const date = getValue(`${namePrefix}.date`, '')

  const providerOptions = caseProviders.map(p => {
    const displayName =
      p.classification === 'Individual'
        ? `${p.firstName || ''} ${p.lastName || ''}${p.suffix ? `, ${p.suffix}` : ''}`.trim()
        : p.name || p.organizationName || ''
    return {
      id: displayName,
      name: displayName,
    }
  })

  if (physician && !providerOptions.some(o => o.id === physician || o.name === physician)) {
    providerOptions.unshift({ id: physician, name: physician })
  }

  if (!isEditing) {
    return (
      <div className="d-flex align-items-start justify-content-between clearfix">
        <div className="w-100 mr-3">
          <div>
            <span className="font-weight-semibold">{name || <em className="text-muted">Untitled</em>}</span>
            {physician && (
              <span className="text-muted ml-3" style={{ fontSize: '0.9em' }}>
                {physician}
              </span>
            )}
            {date && (
              <span className="text-muted ml-3" style={{ fontSize: '0.9em' }}>
                {date}
              </span>
            )}
          </div>
        </div>
        <div className="d-flex align-items-center" style={{ gap: '8px', flexShrink: 0 }}>
          <button type="button" className="btn btn-link btn-sm p-0" onClick={onEdit} title="Edit this diagnosis">
            <i className="fas fa-pen" aria-hidden="true" />
          </button>
          <RemoveButton id={`remove-${namePrefix}`} className="btn-sm" onClick={handleRemove}>
            Remove
          </RemoveButton>
        </div>
      </div>
    )
  }

  return (
    <div className="clearfix">
      <div className="form-row">
        <div className="form-group col-md-5">
          <label className="form-label" htmlFor={`${namePrefix}.name`}>
            Diagnosis
          </label>
          <input
            type="text"
            className="form-control"
            maxLength={validation.maxLengths.name}
            {...createInputProps(`${namePrefix}.name`)}
          />
          <ErrorMessage name={`${namePrefix}.name`} className="d-md-none" />
        </div>
        <div className="form-group col-md-4">
          <label className="form-label" htmlFor={`${namePrefix}.physician`}>
            Physician
          </label>
          <select className="form-control" {...createInputProps(`${namePrefix}.physician`)}>
            <option value="">Select a physician...</option>
            {providerOptions.map((opt, i) => (
              <option key={i} value={opt.name}>
                {opt.name}
              </option>
            ))}
          </select>
          <ErrorMessage name={`${namePrefix}.physician`} className="d-md-none" />
        </div>
        <div className="form-group col-md-3">
          <label className="form-label" htmlFor={`${namePrefix}.date`}>
            Date
          </label>
          <div className="d-flex">
            <DatePicker {...createInputProps(`${namePrefix}.date`)} />
          </div>
        </div>
        <ErrorMessage name={`${namePrefix}.name`} className="hide-sm-down" />
        <ErrorMessage name={`${namePrefix}.physician`} className="hide-sm-down" />
        <div></div>
      </div>

      <div className="form-row mb-2 mt-3">
        <div className="col-12 d-flex align-items-end justify-content-end pb-1" style={{ gap: '8px' }}>
          <button type="button" className="btn btn-outline-secondary btn-sm" onClick={onDone}>
            Done
          </button>
          <RemoveButton id={`remove-${namePrefix}`} className="btn-sm" onClick={handleRemove}>
            Remove
          </RemoveButton>
        </div>
      </div>
    </div>
  )
}
